import { useMutation } from '@tanstack/react-query';
import { ApiError } from '@src/services/api';
import { ApiTypes, CollectionView, MediaView } from 'common';
import { DEPRECATED_QUERY_KEYS, queryClient, QUERY_KEYS } from '@src/query';
import {
  setCollectionUserPermissions, updateCollection, addMediasToCollection, removeFromCollection, lockCollection,
  updateCollectionQuery,
  updateCollectionThumbnail,
  setCollectionAsFavorite,
} from './api';
import { setQueryCollections } from './useQueryCollections';
import { Mql } from '@adalong/schemas';

export const useMutationCollectionPermissions = () => useMutation<CollectionView, ApiError, {
  access: boolean
  collection: string
  edit: boolean
  user: string
}>({
  mutationFn: (params) => setCollectionUserPermissions(params),
  onSuccess: async (collection) => {
    const key = QUERY_KEYS.collection(collection._id);
    queryClient.invalidateQueries(key);
    return collection;
  },
});

export const useMutationCollection = () => useMutation<CollectionView, ApiError, Parameters<typeof updateCollection>[0]>({
  mutationFn: (partialCollection) => updateCollection(partialCollection),
  onSuccess: async (res, partialCollection) => {
    const key = QUERY_KEYS.collection(partialCollection._id);
    const current = queryClient.getQueryData<CollectionView[]>([DEPRECATED_QUERY_KEYS.COLLECTIONS]) ?? [];
    setQueryCollections(current.map((c) => (c._id === res._id ? res : c)));
    queryClient.invalidateQueries({ queryKey: [DEPRECATED_QUERY_KEYS.COLLECTIONS] });
    queryClient.invalidateQueries({ queryKey: key });
  },
});

export const useMutationCollectionLock = () => useMutation<CollectionView, ApiError, Parameters<typeof lockCollection>[0]>({
  mutationFn: (partialCollection) => lockCollection(partialCollection),
  onSuccess: async (res, partialCollection) => {
    const key = QUERY_KEYS.collection(partialCollection._id);
    const current = queryClient.getQueryData<CollectionView[]>([DEPRECATED_QUERY_KEYS.COLLECTIONS]) ?? [];
    setQueryCollections(current.map((c) => (c._id === res._id ? res : c)));
    queryClient.invalidateQueries({ queryKey: [DEPRECATED_QUERY_KEYS.COLLECTIONS] });
    queryClient.invalidateQueries({ queryKey: key });
  },
});


export const useMutationCollectionAddMedia = () => {
  const mutation = useMutation<CollectionView, ApiError, { collectionId: string; mediaIds: string[] }>({
    mutationFn: ({ collectionId, mediaIds }) => addMediasToCollection({ collectionId, mediaIds }),
    onSuccess: async (res, { collectionId, mediaIds }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collectionsMedia() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collection(collectionId) });
      queryClient.invalidateQueries({ queryKey: [DEPRECATED_QUERY_KEYS.COLLECTION_CONTENT_NUMBER] });
      mediaIds.forEach((mediaId) => {
        queryClient.setQueryData<MediaView | undefined>(QUERY_KEYS.media(mediaId), (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            collections: current.collections.includes(collectionId)
              ? current.collections
              : [...current.collections, collectionId],
          };
        });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.media(mediaId) });
      });
    },
  });

  const addMediaInBatches = async (collectionId: string, mediaIds: string[], batchSize: number = 100) => {
    for (let i = 0; i < mediaIds.length; i += batchSize) {
      const batch = mediaIds.slice(i, i + batchSize);
      await mutation.mutateAsync({ collectionId, mediaIds: batch });
    }
  };

  return { ...mutation, addMediaInBatches };
};

export const useMutationCollectionRemoveMedia = () => useMutation<CollectionView, ApiError, ApiTypes.ParamsRemoveMediaFromCollection>({
  mutationFn: ({ collectionId, mediaIds }) => removeFromCollection({
    collectionId,
    mediaIds,
  }),
  onSuccess: async (res, { collectionId }) => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collectionsMedia() });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collection(collectionId) });
    queryClient.invalidateQueries({ queryKey: [DEPRECATED_QUERY_KEYS.COLLECTION_CONTENT_NUMBER] });
  },
});

export const useMutationCollectionUpdateQuery = () => useMutation<CollectionView, ApiError, {
  collectionId: string;
  query: Mql | null;
  needs_reset?: boolean;
  reset_in_progress?: boolean;
}>({
  mutationFn: ({ collectionId, query, needs_reset, reset_in_progress }) => updateCollectionQuery({
    collectionId,
    query,
    needs_reset,
    reset_in_progress,
  }),
  onSuccess: async (res, { collectionId }) => {
    const key = QUERY_KEYS.collection(collectionId);
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: [DEPRECATED_QUERY_KEYS.COLLECTIONS] });
  },
});

export const useMutationCollectionUpdateThumbnail = () => useMutation<CollectionView, ApiError, { collectionId: string; mediaId: string }>({
  mutationFn: ({ collectionId, mediaId }) => updateCollectionThumbnail({ collectionId, mediaId }),
  onSuccess: async (res, { collectionId }) => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collectionsMedia() });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.collection(collectionId) });
    queryClient.invalidateQueries({ queryKey: [DEPRECATED_QUERY_KEYS.COLLECTION_CONTENT_NUMBER] });
  },
});

export const useMutationCollectionSetAsFavorite = () => useMutation<CollectionView, ApiError, { collectionId: string; isFavorite: boolean }>({
  mutationFn: ({ collectionId, isFavorite }) => setCollectionAsFavorite({ collectionId, isFavorite }),
  onSuccess: async (res, { collectionId }) => {
    const key = QUERY_KEYS.collection(collectionId);
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: [DEPRECATED_QUERY_KEYS.COLLECTIONS] });
  },
});
